import React, { useState } from 'react';
import { WD_LONG, longDay, ordinal, parseDay, repeatText } from '../../lib/quickAdd';
import { askToNotify } from '../../lib/osNotify';
import { Icon } from './icons';

/*
 * The editor's Repeat and Reminders (batch 3).
 *
 * Gordon wanted "every Monday" and "1st of the month", and a nudge when
 * something is due. Quick add reads both from a sentence; these are the same
 * two things by hand, Todoist's way: a short list of the repeats people
 * actually use, named for THIS to-do ("Weekly on Thursday" when it is due on
 * a Thursday), and "Custom…" for the rest. Reminders are a few presets
 * counted back from when it is due, or a date and time of their own.
 *
 * Both edit plain values in the contract's shapes and hand them up; the page
 * decides what, if anything, changed and needs sending.
 */

const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Monday first, as a week is here.
const WEEK = [1, 2, 3, 4, 5, 6, 0];
const UNITS = [
  { freq: 'daily', one: 'day', many: 'days' },
  { freq: 'weekly', one: 'week', many: 'weeks' },
  { freq: 'monthly', one: 'month', many: 'months' },
  { freq: 'yearly', one: 'year', many: 'years' }
];

/**
 * A repeat as it is compared: only the keys that mean something, in one
 * order, so the server's echo of what was sent (with an id, or a field this
 * page does not know) does not read as a change.
 */
export function cleanRepeat(r) {
  if (!r || !r.freq) return null;
  const out = { freq: r.freq, interval: Math.max(1, Number(r.interval) || 1) };
  if (r.freq === 'weekly' && Array.isArray(r.weekdays) && r.weekdays.length) {
    out.weekdays = [...new Set(r.weekdays.map(Number))].sort((a, b) => a - b);
  }
  if (r.freq === 'monthly' && r.monthday) out.monthday = Number(r.monthday);
  out.from = r.from === 'done' ? 'done' : 'due';
  if (r.until) out.until = String(r.until).slice(0, 10);
  return out;
}

/** A reminder as it is sent: an offset when it has one, otherwise its moment. */
export function cleanReminder(r) {
  if (r && r.offset_minutes !== null && r.offset_minutes !== undefined && r.offset_minutes !== ''
      && Number.isFinite(Number(r.offset_minutes))) {
    return { offset_minutes: Number(r.offset_minutes) };
  }
  if (r && r.remind_at) return { remind_at: new Date(r.remind_at).toISOString() };
  return null;
}
export const cleanReminders = (list) => (Array.isArray(list) ? list.map(cleanReminder).filter(Boolean) : []);

export const MAX_REMINDERS = 5;
export const REMINDER_PRESETS = [
  { minutes: 0, label: 'At time of due' },
  { minutes: 10, label: '10 min before' },
  { minutes: 30, label: '30 min before' },
  { minutes: 60, label: '1 hour before' },
  { minutes: 1440, label: '1 day before' }
];

const pad = (n) => String(n).padStart(2, '0');

/** How one reminder reads: "30 min before", "At the time", "On Wed 21 Oct 14:00". */
export function reminderLabel(r) {
  const c = cleanReminder(r);
  if (!c) return '';
  if ('offset_minutes' in c) {
    const m = c.offset_minutes;
    const preset = REMINDER_PRESETS.find((p) => p.minutes === m);
    if (m === 0) return 'At the time';
    if (preset) return preset.label;
    if (m % 1440 === 0) return `${m / 1440} days before`;
    if (m % 60 === 0) return `${m / 60} hours before`;
    return `${m} min before`;
  }
  const d = new Date(c.remind_at);
  return `On ${longDay(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ------------------------------------------------------------- Repeat */

/** The presets, for a to-do due on `anchor` (its date, or today's). */
function presets(anchor) {
  const d = parseDay(anchor);
  return [
    { key: 'daily', label: 'Daily', repeat: { freq: 'daily', interval: 1, from: 'due' } },
    { key: 'weekdays', label: 'Weekdays (Mon–Fri)',
      repeat: { freq: 'weekly', interval: 1, weekdays: [1, 2, 3, 4, 5], from: 'due' } },
    { key: 'weekly', label: `Weekly on ${WD_LONG[d.getDay()]}`,
      repeat: { freq: 'weekly', interval: 1, weekdays: [d.getDay()], from: 'due' } },
    { key: 'monthly', label: `Monthly on the ${ordinal(d.getDate())}`,
      repeat: { freq: 'monthly', interval: 1, monthday: d.getDate(), from: 'due' } },
    { key: 'yearly', label: `Yearly on ${d.getDate()} ${MON_SHORT[d.getMonth()]}`,
      repeat: { freq: 'yearly', interval: 1, from: 'due' } }
  ];
}

const same = (a, b) => JSON.stringify(cleanRepeat(a)) === JSON.stringify(cleanRepeat(b));

export function RepeatPicker({ value, onChange, dueDate, todayIso, now }) {
  const anchor = dueDate || todayIso;
  const list = presets(anchor);
  const hit = value ? list.find((p) => same(p.repeat, value)) : null;
  const [custom, setCustom] = useState(Boolean(value) && !hit);
  const choice = !value ? (custom ? 'custom' : '') : hit && !custom ? hit.key : 'custom';
  const cur = cleanRepeat(value);
  const set = (patch) => onChange(cleanRepeat({ ...(cur || { freq: 'weekly', interval: 1 }), ...patch }));

  const pick = (key) => {
    if (key === '') { setCustom(false); onChange(null); return; }
    if (key === 'custom') {
      setCustom(true);
      if (!value) onChange(cleanRepeat({ freq: 'weekly', interval: 1, weekdays: [parseDay(anchor).getDay()] }));
      return;
    }
    setCustom(false);
    onChange(cleanRepeat(list.find((p) => p.key === key).repeat));
  };

  const unit = UNITS.find((u) => u.freq === (cur && cur.freq)) || UNITS[1];
  const n = cur ? cur.interval : 1;
  const days = new Set(cur && cur.weekdays ? cur.weekdays : []);

  return (
    <fieldset className="tdr-box tdr-repeat">
      <legend>Repeat</legend>
      <label className="tdr-line"><span className="tdr-sr">Repeat</span>
        <select aria-label="Repeat" value={choice} onChange={(e) => pick(e.target.value)}>
          <option value="">Does not repeat</option>
          {list.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          <option value="custom">Custom…</option>
        </select>
      </label>
      {choice === 'custom' && cur && (
        <div className="tdr-custom">
          <div className="tdr-every">
            <span>Every</span>
            <input type="number" min={1} max={999} inputMode="numeric" aria-label="Repeat every"
                   value={n} onChange={(e) => set({ interval: Math.max(1, Math.min(999, Number(e.target.value) || 1)) })} />
            <select aria-label="Repeat unit" value={unit.freq}
                    onChange={(e) => set({ freq: e.target.value, weekdays: undefined, monthday: undefined })}>
              {UNITS.map((u) => <option key={u.freq} value={u.freq}>{n === 1 ? u.one : u.many}</option>)}
            </select>
          </div>
          {unit.freq === 'weekly' && (
            <div className="tdr-days" role="group" aria-label="On these days">
              {WEEK.map((d) => (
                <button key={d} type="button" className={`tdr-day${days.has(d) ? ' is-on' : ''}`}
                        aria-pressed={days.has(d)} aria-label={`Repeat on ${WD_LONG[d]}`}
                        onClick={() => {
                          const next = new Set(days);
                          if (next.has(d)) next.delete(d); else next.add(d);
                          set({ weekdays: [...next] });
                        }}>
                  {WD_LONG[d].slice(0, 2)}
                </button>
              ))}
            </div>
          )}
          {unit.freq === 'monthly' && (
            <label className="tdr-line"><span>On</span>
              <select aria-label="Day of the month" value={cur.monthday ? String(cur.monthday) : ''}
                      onChange={(e) => set({ monthday: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">the due date's day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d)}>the {ordinal(d)}</option>
                ))}
                <option value="-1">the last day</option>
              </select>
            </label>
          )}
          <label className="tdr-line"><span>Count from</span>
            <select aria-label="Count from" value={cur.from} onChange={(e) => set({ from: e.target.value })}>
              <option value="due">the due date</option>
              <option value="done">when it is done</option>
            </select>
          </label>
          <label className="tdr-line"><span>Until</span>
            <input type="date" aria-label="Repeat until" value={cur.until || ''}
                   onChange={(e) => set({ until: e.target.value || undefined })} />
          </label>
        </div>
      )}
      {cur && (
        <p className="tdr-summary" data-repeat-summary="">
          <Icon name="repeat" size={13} />{repeatText(cur, { now, dueDate: anchor })}
          {!dueDate && <span className="tdr-note"> - starting {longDay(anchor)}</span>}
        </p>
      )}
    </fieldset>
  );
}

/* ---------------------------------------------------------- Reminders */

const localInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export function ReminderPicker({ value, onChange, dueDate, dueTime }) {
  const [at, setAt] = useState(null);   // the "On a date & time…" field, while open
  const list = Array.isArray(value) ? value : [];
  const full = list.length >= MAX_REMINDERS;
  const offsets = new Set(cleanReminders(list).filter((r) => 'offset_minutes' in r).map((r) => r.offset_minutes));
  const add = (r) => {
    if (full) return;
    onChange([...list, r]);
    // The moment somebody has just asked to be reminded is the one moment
    // asking for desktop notifications makes sense (see lib/osNotify).
    askToNotify();
  };

  return (
    <fieldset className="tdr-box tdr-reminders">
      <legend>Reminders</legend>
      {list.length > 0 && (
        <ul className="tdr-rems" aria-label="Reminders set">
          {list.map((r, i) => {
            const label = reminderLabel(r);
            return (
              <li key={r.id || `${i}-${label}`} className="tdr-rem" data-reminder-chip="">
                <Icon name="bell" size={12} />{label}
                <button type="button" aria-label={`Remove reminder: ${label}`} title="Remove this reminder"
                        onClick={() => onChange(list.filter((_, j) => j !== i))}><Icon name="close" size={12} /></button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="tdr-line">
        <select aria-label="Add a reminder" value="" disabled={full}
                onChange={(e) => {
                  const k = e.target.value;
                  if (k === 'at') {
                    const base = dueDate ? parseDay(dueDate) : new Date();
                    base.setHours(dueTime ? Number(dueTime.slice(0, 2)) : 9, dueTime ? Number(dueTime.slice(3, 5)) : 0, 0, 0);
                    setAt(localInput(base));
                  } else if (k !== '') add({ offset_minutes: Number(k) });
                }}>
          <option value="">{full ? 'Five is the most' : 'Add a reminder…'}</option>
          {REMINDER_PRESETS.map((p) => (
            <option key={p.minutes} value={String(p.minutes)} disabled={!dueDate || offsets.has(p.minutes)}>
              {p.label}
            </option>
          ))}
          <option value="at">On a date &amp; time…</option>
        </select>
      </div>
      {at !== null && !full && (
        <div className="tdr-line tdr-at">
          <input type="datetime-local" aria-label="Remind me at" value={at} onChange={(e) => setAt(e.target.value)} />
          <button type="button" className="btn btn-secondary btn-small" disabled={!at}
                  onClick={() => { add({ remind_at: new Date(at).toISOString() }); setAt(null); }}>
            Add this reminder
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setAt(null)}>Cancel</button>
        </div>
      )}
      {!dueDate && (
        <p className="tdr-note">Give it a due date to be reminded before it, or pick a date &amp; time.</p>
      )}
      {dueDate && !dueTime && offsets.size > 0 && (
        <p className="tdr-note" data-from-nine="">No time is set, so these count from 09:00 on the due date.</p>
      )}
      {full && <p className="tdr-note">Five reminders is the most one to-do can carry.</p>}
    </fieldset>
  );
}
